import { useState } from "react";
import { cn } from "@/utils";
import { StorySectionHeader } from "./story-section-header";
import type { StorySectionState } from "./story-section-header";

export type { StorySectionState };

export interface StorySectionProps {
  step: number;
  /** Optional icon node replacing step number in badge */
  icon?: React.ReactNode;
  state: StorySectionState;
  title: string;
  /** Short metadata shown inline after title */
  meta?: React.ReactNode;
  /** Seed collapsed state; controlled internally after first render.
   *  When `onToggle` is supplied the component becomes fully controlled:
   *  `defaultCollapsed` is used as the current value and `onToggle` is
   *  called on header click instead of the internal setter. */
  defaultCollapsed?: boolean;
  /** Optional controlled-mode toggle handler. When provided, collapse state
   *  is driven externally (e.g. from a Zustand persist store). */
  onToggle?: () => void;
  children: React.ReactNode;
  className?: string;
}

/**
 * StorySection — accordion step in a vertical storyline.
 *
 * Layout: badge + title row / expandable body indented under badge. Parent
 * `.story-rail` container supplies the gradient connector line via `::before`;
 * each section only provides left-padding so the body aligns under the badge.
 *
 * Keyframe: body uses `expand` (fadeIn + translateY -4px→0) defined in
 * utilities.css. Header chevron rotates via CSS transition — no keyframe needed.
 */
export function StorySection({
  step,
  icon,
  state,
  title,
  meta,
  defaultCollapsed = false,
  onToggle,
  children,
  className,
}: StorySectionProps) {
  // Uncontrolled mode: internal state seeded from defaultCollapsed.
  // Controlled mode: when onToggle is provided, defaultCollapsed is the
  // authoritative current value and the internal setter is bypassed.
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isControlled = onToggle !== undefined;
  const collapsed = isControlled ? defaultCollapsed : internalCollapsed;
  const handleToggle = isControlled ? onToggle : () => setInternalCollapsed((c) => !c);

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Header row */}
      <StorySectionHeader
        step={step}
        icon={icon}
        state={state}
        title={title}
        meta={meta}
        collapsed={collapsed}
        onToggle={handleToggle}
      />

      {/* Body — hidden when collapsed, fade-in expand animation on open */}
      {!collapsed && (
        <div
          className={cn(
            "pl-[34px] pb-3 pt-1",
            "animate-[expand_180ms_cubic-bezier(0.2,0,0,1)_both]"
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * StoryRail — wrapper that renders the gradient connector line behind sections.
 * Use this as the parent container in the drawer body.
 *
 * ```tsx
 * <StoryRail>
 *   <StorySection step={1} state="done" title="Folder" ... />
 *   <StorySection step={2} state="active" title="Processing" ... />
 * </StoryRail>
 * ```
 */
export function StoryRail({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex flex-col gap-1",
        // Gradient rail — 2px line at left-[11px] connecting all step badges.
        // Starts/ends 24px from top/bottom so it doesn't overshoot first/last badge.
        // Opacity 0.4: visible enough to read as a connected sequence without
        // competing with section content for attention.
        "before:content-[''] before:absolute before:left-[11px] before:top-6 before:bottom-6",
        "before:w-0.5 before:rounded-[var(--radius-full)]",
        "before:bg-gradient-to-b before:from-[var(--success)] before:via-[var(--primary)] before:to-[var(--purple)]",
        "before:opacity-[0.4]",
        className
      )}
    >
      {children}
    </div>
  );
}
